/**
 * The words of the chef print packet's Orders sheets that do not already
 * live in a screen's copy module: which order the school sheets print in,
 * the stop line each sheet carries, and a deliverable in the Orders card's
 * words laid out for paper.
 *
 * Abner, Sep 25 (the print canvas, option C and route 1):
 * - School sheets print in DRIVING order and each names its stop ("Stop 2 ·
 *   Unload 8:55 AM · Window 7:00 to 9:00 AM"), so the piles stack the way the
 *   van unloads. With no route, the Orders screen's order and no stop line.
 * - A row reads like the card: the contents first, the package after them,
 *   no pan count and no "(N in all)" totals. A unitized row is its containers
 *   as a multiplier and the meal's name ("16x" + "Creamy Cheddar Mac (White
 *   Penne) • Steamed Seasonal Veggie"), then what goes in EACH container.
 *
 * Every word comes from the screen's own functions (`describeDeliverable`,
 * `unitizedLead`, `packagingLine`, `splitQuantity`, `titleCaseFood`, the
 * Delivery page's clock and window words), so paper and screen cannot drift.
 * Pure; unit-tested.
 */

import type { DeliveryRouteDay, DeliveryRouteStop } from '@/api/delivery-route'
import { describeDeliverable, packagingLine, splitQuantity, unitizedLead } from '@/lib/chefs/deliverable-copy'
import { clockLabel, windowLabel } from '@/lib/chefs/delivery-route'
import { temperatureLabel } from '@/lib/chefs/format'
import { titleCaseFood } from '@/lib/chefs/kitchen-name'
import type { Deliverable } from '@/lib/chefs/types'
import { dryRunServiceTypeLabel, portionTierTagLabel } from '@/lib/chefs/types'

// ---------------------------------------------------------------------------
// Sheet order and the stop line
// ---------------------------------------------------------------------------

/**
 * The stop line under a school's name: "Stop 2 · Unload 8:55 AM · Window
 * 7:00 to 9:00 AM". The unload time is the plan's (it needs drive times);
 * with none the line is the stop and its window, "Stop 1 · Window 7:00 to
 * 9:00 AM", and with no window either, "Stop 1".
 */
export function sheetStopLine(n: number, stop: Pick<DeliveryRouteStop, 'unloadAt' | 'window'>): string {
  const parts = [`Stop ${n}`]
  const unload = clockLabel(stop.unloadAt)
  if (unload) {
    parts.push(`Unload ${unload}`)
  }
  const window = windowLabel(stop.window)
  if (window) {
    parts.push(`Window ${window}`)
  }
  return parts.join(' · ')
}

/** A school sheet in the packet: the campus, and its place on the route when it has one. */
export interface OrderedSheet<T> {
  campus: T
  /** 1-based stop number; null when the school is not driven today (a dry run) or the route is unknown. */
  stop: number | null
  /** `sheetStopLine`, or null with no stop. */
  stopLine: string | null
}

/**
 * The day's school sheets in the order the van unloads: the route's stops
 * first, in its order, then the schools that are not driven (dry runs, in
 * the route's order), then anything the route does not know, in the order it
 * came in (the Orders screen's). With no route day every sheet keeps the
 * screen's order and no stop line.
 */
export function sheetsInRouteOrder<T extends { id: string }>(
  campuses: readonly T[],
  routeDay: Pick<DeliveryRouteDay, 'stops' | 'notDriven'> | null | undefined,
): OrderedSheet<T>[] {
  if (!routeDay) {
    return campuses.map((campus) => ({ campus, stop: null, stopLine: null }))
  }
  const byId = new Map(campuses.map((campus) => [campus.id, campus]))
  const placed = new Set<string>()
  const sheets: OrderedSheet<T>[] = []
  routeDay.stops.forEach((stop, i) => {
    const campus = byId.get(stop.schoolLocationId)
    if (campus && !placed.has(campus.id)) {
      placed.add(campus.id)
      sheets.push({ campus, stop: i + 1, stopLine: sheetStopLine(i + 1, stop) })
    }
  })
  for (const stop of routeDay.notDriven) {
    const campus = byId.get(stop.schoolLocationId)
    if (campus && !placed.has(campus.id)) {
      placed.add(campus.id)
      sheets.push({ campus, stop: null, stopLine: null })
    }
  }
  for (const campus of campuses) {
    if (!placed.has(campus.id)) {
      sheets.push({ campus, stop: null, stopLine: null })
    }
  }
  return sheets
}

// ---------------------------------------------------------------------------
// A deliverable, for paper
// ---------------------------------------------------------------------------

/** A number and its words, weighted apart on paper: "22.2" + "gal" + "Unsweetened Canned Applesauce". */
export interface PrintAmountLine {
  numeral: string
  unit: string
  name: string
}

export interface PrintDeliverableWords {
  /**
   * What leads the row. Bulk and loose: each food with its total, the card's
   * stacks. Unitized: one line, the containers as a multiplier and the meal's
   * name (`unitizedLead`).
   */
  lead: PrintAmountLine[]
  /** The package as the card's band names it, plural and with no count: "Bulk Trays", "Jugs". */
  packageLine: string
  /** Unitized only: what goes in EACH container, the band's one-line rows. Null on bulk and loose rows. */
  each: PrintAmountLine[] | null
  /** The card's flags as words: the portion tier, "Field trip", "Holiday treat · free", the dry-run label. */
  tags: string[]
  /** "Hot", "Chilled", "Room". */
  temperature: string
}

function amountLine(qty: string, name: string): PrintAmountLine {
  const { numeral, unit } = splitQuantity(qty)
  return { numeral, unit, name }
}

/**
 * One deliverable in the Orders card's words, for the printed sheet's strip
 * row: the same stacks, band and flags as `DeliverableCard`, without the
 * photos (paper), the pan count or the order totals (the card has neither).
 */
/** A row's flags as words: the portion tier, "Field trip", "Holiday treat · free", the dry-run label. */
export function deliverableTags(
  d: Pick<
    Deliverable,
    'showPortionTier' | 'portionTier' | 'isFieldTrip' | 'isHolidayTreat' | 'isDryRun' | 'dryRunServiceType'
  >,
): string[] {
  const tags: string[] = []
  if (d.showPortionTier && d.portionTier) {
    tags.push(portionTierTagLabel(d.portionTier))
  }
  if (d.isFieldTrip) {
    tags.push('Field trip')
  }
  if (d.isHolidayTreat) {
    tags.push('Holiday treat · free')
  }
  if (d.isDryRun) {
    tags.push(dryRunServiceTypeLabel(d.dryRunServiceType))
  }
  return tags
}

export function printDeliverableWords(d: Deliverable): PrintDeliverableWords {
  const copy = describeDeliverable(d)
  const containers = copy.mode === 'containers' ? copy.count : null
  const tags = deliverableTags(d)
  const contents = copy.contents.map((line) => amountLine(line.qty, titleCaseFood(line.label)))
  if (containers === null) {
    return {
      lead: contents,
      packageLine: packagingLine(copy),
      each: null,
      tags,
      temperature: temperatureLabel(d.temperature),
    }
  }
  const unitized = unitizedLead(d, containers)
  return {
    lead: [amountLine(unitized.qty, unitized.label)],
    packageLine: packagingLine(copy),
    each: contents,
    tags,
    temperature: temperatureLabel(d.temperature),
  }
}
